import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useAuth } from '../hooks/useAuth';
import { ROLE_LABELS, ROLE_COLORS } from '../lib/roles';
import { getRecentActivity } from '../lib/analytics';
import { listScoresForUser, type CompetencyScoreDto } from '../lib/competencyScores';
import { listCompetencies, type CompetencyDto } from '../lib/competencies';
import { DEFAULT_COMPANY_ID } from '../lib/config';
import { API_BASE_URL } from '../lib/config';
import { apiFetch } from '../lib/apiFetch';
import { toast } from 'sonner';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <ProfileContent />
    </ProtectedRoute>
  );
}

// ─── Password change form ─────────────────────────────────────────────────────

function PasswordChangeForm({ userId }: { userId: string }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/users/${userId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Password changed successfully');
        setSuccess(true);
        setCurrent('');
        setNext('');
        setConfirm('');
      } else {
        const msg = json.error ?? 'Failed to change password.';
        toast.error(msg);
        setError(msg);
      }
    } catch {
      toast.error('Network error. Please try again.');
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          Password changed successfully.
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
        <input
          type="password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          placeholder="••••••••"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
        <input
          type="password"
          required
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          placeholder="Min. 8 characters"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
        <input
          type="password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          placeholder="Repeat new password"
        />
      </div>
      <button
        type="submit"
        disabled={saving}
        className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Change Password'}
      </button>
    </form>
  );
}

// ─── Competency bar ───────────────────────────────────────────────────────────

function CompetencyBar({ name, code, score }: { name: string; code: string; score: number }) {
  // score is 1–5; convert to percentage
  const pct = Math.round((score / 5) * 100);
  const color =
    score >= 4 ? 'bg-emerald-500' : score >= 3 ? 'bg-blue-500' : score >= 2 ? 'bg-amber-400' : 'bg-red-400';

  return (
    <div className="flex items-center gap-3">
      <div className="w-32 shrink-0">
        <p className="text-xs font-medium text-gray-700 truncate" title={name}>{name}</p>
        <p className="text-[10px] font-mono text-gray-400">{code}</p>
      </div>
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-8 text-right tabular-nums">
        {score.toFixed(1)}<span className="text-gray-400 font-normal">/5</span>
      </span>
    </div>
  );
}

// ─── My Competencies tab ──────────────────────────────────────────────────────

function MyCompetenciesTab({ userId, companyId }: { userId: string; companyId: string }) {
  const [scores, setScores] = useState<CompetencyScoreDto[]>([]);
  const [competencies, setCompetencies] = useState<CompetencyDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listScoresForUser(userId, companyId, true),
      listCompetencies({ companyId }),
    ]).then(([scoresRes, compsRes]) => {
      if (cancelled) return;
      setLoading(false);
      if (scoresRes.success && scoresRes.data) setScores(scoresRes.data.scores);
      if (compsRes.success && compsRes.data) setCompetencies(compsRes.data.competencies);
    });
    return () => { cancelled = true; };
  }, [userId, companyId]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (scores.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400">
        <p className="text-sm font-medium text-gray-600 mb-1">No competency scores yet</p>
        <p className="text-xs">Scores are derived from assessments and performance metrics.</p>
      </div>
    );
  }

  // Join scores to competency definitions for names
  const enriched = scores
    .map((s) => {
      const comp = competencies.find((c) => c.id === s.competencyId);
      return { ...s, name: comp?.name ?? s.competencyId.slice(0, 8), code: comp?.code ?? '—' };
    })
    .sort((a, b) => b.score - a.score);

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Latest competency scores derived from your assessments and metrics. Higher is better (1–5 scale).
      </p>
      <div className="space-y-3">
        {enriched.map((s) => (
          <CompetencyBar key={s.id} name={s.name} code={s.code} score={s.score} />
        ))}
      </div>
      <p className="text-[10px] text-gray-400 pt-1">
        Last assessed: {new Date(enriched[0]?.assessedAt ?? '').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        &nbsp;·&nbsp;Source: {enriched[0]?.source}
      </p>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

function ProfileContent() {
  const { user, logout } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const [tab, setTab] = useState<'overview' | 'competencies' | 'security'>('overview');
  const [activity, setActivity] = useState<Array<{ id: string; type: string; title: string; createdAt: string }>>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    if (!user?.companyId) return;
    let cancelled = false;
    setActivityLoading(true);
    getRecentActivity(user.companyId).then((res) => {
      if (cancelled) return;
      setActivityLoading(false);
      if (res.success && res.data) setActivity(res.data.activity);
    });
    return () => { cancelled = true; };
  }, [user?.companyId]);

  if (!user) return null;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'competencies', label: 'My Competencies' },
    { key: 'security', label: 'Security' },
  ] as const;

  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <button
          type="button"
          onClick={logout}
          className="text-sm text-red-600 hover:text-red-700 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50"
        >
          Logout
        </button>
      </div>

      {/* Avatar + name */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-700">
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
          <p className="text-sm text-gray-500">{user.email}</p>
          <span className={`mt-1 inline-block text-xs px-2 py-0.5 rounded-full font-medium ${(ROLE_COLORS as any)[user.role]}`}>
            {(ROLE_LABELS as any)[user.role]}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-4">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Details</h3>
            <dl className="space-y-3">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Full Name</dt>
                <dd className="text-gray-800 font-medium">{user.name}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Email</dt>
                <dd className="text-gray-800">{user.email}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Role</dt>
                <dd className="text-gray-800">{(ROLE_LABELS as any)[user.role]}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Company ID</dt>
                <dd className="text-gray-400 font-mono text-xs">{user.companyId?.slice(0, 8)}…</dd>
              </div>
            </dl>
          </div>

          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</h3>
            {activityLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
              </div>
            ) : activity.length === 0 ? (
              <p className="text-xs text-gray-400">No recent activity.</p>
            ) : (
              <ul className="space-y-2">
                {activity.map((item) => (
                  <li key={item.id} className="flex items-start gap-2 text-xs">
                    <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-700 truncate">{item.title}</p>
                      <p className="text-gray-400">{new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* My Competencies tab */}
      {tab === 'competencies' && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">My Competencies</h3>
          <MyCompetenciesTab userId={user.id} companyId={companyId} />
        </div>
      )}

      {/* Security tab */}
      {tab === 'security' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Change Password</h3>
            <PasswordChangeForm userId={user.id} />
          </div>
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Session</h3>
            <p className="text-xs text-gray-400 mb-3">
              You are currently logged in. Logging out will clear your session.
            </p>
            <button
              type="button"
              onClick={logout}
              className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
